import { AdPlacement } from '@openx/types/adUnit.ts';
import { DistributionChannel } from '@openx/types/distributionChannel.ts';
import type { FormatTypeData } from '@openx/types/targeting/formatType.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import {
  type TechnologyAndDevicesFormState,
  TechnologyAndDevicesOption,
} from '@openx/types/targeting/technologyAndDevices.ts';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { isEmptyState as isTechnographicEmpty } from '../../Technographic/utils.ts';

export type CtvValidationReturnType = {
  setFormatType: Dispatch<SetStateAction<FormatTypeData | null>>;
  setTechnographic: Dispatch<SetStateAction<TechnologyAndDevicesFormState | null>>;
  shouldDisplayWarningInTechnographic: boolean;
  shouldDisplayWarningInFormatType: boolean;
  isTechnographicOpen: boolean;
  isFormatTypeOpen: boolean;
  setIsTechnographicOpen: Dispatch<SetStateAction<boolean>>;
  setIsFormatTypeOpen: Dispatch<SetStateAction<boolean>>;
};

const CtvDeviceTypes = {
  CONNECTED_TV: '5',
  SET_TOP_BOX: '6',
} as const;

export const useCtvValidation = (): CtvValidationReturnType => {
  const [formatType, setFormatType] = useState<FormatTypeData | null>(null);
  const [technographic, setTechnographic] = useState<TechnologyAndDevicesFormState | null>(null);
  const [isTechnographicOpen, setIsTechnographicOpen] = useState<boolean>(false);
  const [isFormatTypeOpen, setIsFormatTypeOpen] = useState<boolean>(false);

  const technographicHasCtvParams = Boolean(
    technographic?.[TechnologyAndDevicesOption.DEVICE_TYPE]?.val?.has(CtvDeviceTypes.CONNECTED_TV) ||
      technographic?.[TechnologyAndDevicesOption.DEVICE_TYPE]?.val?.has(CtvDeviceTypes.SET_TOP_BOX)
  );

  const technographicMatchesCtvParams = Boolean(
    technographic?.[TechnologyAndDevicesOption.DEVICE_TYPE]?.val?.has(CtvDeviceTypes.CONNECTED_TV) &&
      technographic?.[TechnologyAndDevicesOption.DEVICE_TYPE]?.val?.has(CtvDeviceTypes.SET_TOP_BOX)
  );

  const technographicIntersects = technographic?.[TechnologyAndDevicesOption.DEVICE_TYPE]?.op === Intersect.INTERSECTS;

  const technographicInNotEmpty = !isTechnographicEmpty(technographic);

  const formatTypeIsNotEmpty = !(formatType === null || (!formatType.ad_placement && !formatType.distribution_channel));

  const formatTypeMatchesCtvParams = Boolean(
    formatType?.ad_placement?.val?.includes(AdPlacement.Video) &&
      formatType?.distribution_channel?.val?.includes(DistributionChannel.App)
  );

  const shouldDisplayWarningInTechnographic =
    !formatTypeMatchesCtvParams && technographicHasCtvParams && technographicIntersects && technographicInNotEmpty;

  const shouldDisplayWarningInFormatType =
    (!technographicHasCtvParams || !technographicMatchesCtvParams) &&
    technographicIntersects &&
    formatTypeIsNotEmpty &&
    formatTypeMatchesCtvParams;

  return {
    isFormatTypeOpen,
    isTechnographicOpen,
    setFormatType,
    setIsFormatTypeOpen,
    setIsTechnographicOpen,
    setTechnographic,
    shouldDisplayWarningInFormatType,
    shouldDisplayWarningInTechnographic,
  };
};
